import React, { useRef, useEffect } from 'react';
import { Box, Typography } from '@mui/material';
import { gsap } from 'gsap';
import { TextPosition, FontFamily } from '@/types';
import { ANIMATION_CONFIG } from '@/config/animations';

interface TextOverlayProps {
    text_content: string;
    background_color?: string;
    isActive?: boolean;
    className?: string;
}

/**
 * Reusable text overlay component with support for positioning
 * All positioning data is now embedded in the text_content JSON
 */
const TextOverlay: React.FC<TextOverlayProps> = ({
    text_content,
    background_color = 'transparent',
    isActive,
    className
}) => {
    const innerRef = useRef<HTMLDivElement>(null);
    const hasAnimated = useRef(false);

    // Parse text_content to extract HTML and positioning data
    let htmlContent = text_content;
    let text_position: TextPosition = 'center';
    let text_width_percent = 100;
    let mobile_text_width_percent = 100;
    let text_offset_x_percent = 0;
    let text_offset_y_percent = 0;
    let line_height = 0.7;

    try {
        const parsed = JSON.parse(text_content);
        if (parsed.html) {
            htmlContent = parsed.html;
            text_position = parsed.text_position || 'center';
            text_width_percent = parsed.text_width_percent || 100;
            mobile_text_width_percent = parsed.mobile_text_width_percent !== undefined ? parsed.mobile_text_width_percent : 100;
            text_offset_x_percent = parsed.text_offset_x_percent !== undefined ? parsed.text_offset_x_percent : 0;
            text_offset_y_percent = parsed.text_offset_y_percent !== undefined ? parsed.text_offset_y_percent : 0;
            line_height = parsed.line_height !== undefined ? parsed.line_height : 0.7;
        }
    } catch {
        // If not JSON, use as-is
        htmlContent = text_content;
    }

    // Animate text when it becomes active (animate inner wrapper, not positioned container)
    useEffect(() => {
        if (isActive && innerRef.current && !hasAnimated.current) {
            hasAnimated.current = true;
            gsap.fromTo(
                innerRef.current,
                { y: 30, opacity: 0 },
                {
                    y: 0,
                    opacity: 1,
                    duration: ANIMATION_CONFIG.components.textSection.duration,
                    ease: ANIMATION_CONFIG.components.textSection.ease,
                    delay: ANIMATION_CONFIG.components.textSection.delay
                }
            );
        } else if (!isActive) {
            hasAnimated.current = false;
            if (innerRef.current) {
                gsap.set(innerRef.current, { y: 30, opacity: 0 });
            }
        }
    }, [isActive]);

    // Helper function to get position styles based on text_position with optional X/Y offsets
    const getPositionStyles = () => {
        const baseStyles: React.CSSProperties = {
            position: 'absolute',
            left: 'initial',
            top: 'initial',
            bottom: 'initial',
            right: 'initial',
            maxWidth: `${text_width_percent}%`,
            width: '100%',
        };

        let positionStyle: React.CSSProperties = {};

        // First, apply the base text_position
        switch (text_position) {
            case 'left':
                positionStyle = { left: '0%', top: '50%', transform: 'translateY(-50%)' };
                break;
            case 'center':
                positionStyle = { left: '50%', top: '50%', transform: 'translate(-50%, -50%)' };
                break;
            case 'right':
                positionStyle = { right: '0%', top: '50%', transform: 'translateY(-50%)' };
                break;
            case 'left-top':
                positionStyle = { left: '0%', top: '0%' };
                break;
            case 'center-top':
                positionStyle = { left: '50%', top: '0%', transform: 'translateX(-50%)' };
                break;
            case 'right-top':
                positionStyle = { right: '0%', top: '0%' };
                break;
            case 'left-bottom':
                positionStyle = { left: '0%', bottom: '0%' };
                break;
            case 'center-bottom':
                positionStyle = { left: '50%', bottom: '0%', transform: 'translateX(-50%)' };
                break;
            case 'right-bottom':
                positionStyle = { right: '0%', bottom: '0%' };
                break;
            default:
                positionStyle = { left: '50%', top: '50%', transform: 'translate(-50%, -50%)' };
        }

        // Then, apply X/Y offsets if they are non-zero
        if (text_offset_x_percent !== 0 || text_offset_y_percent !== 0) {
            const styles = { ...baseStyles, ...positionStyle };

            // Apply X offset
            if (text_offset_x_percent !== 0) {
                if (styles.right !== undefined && styles.right !== 'initial') {
                    const rightValue = parseFloat(String(styles.right));
                    styles.right = `${rightValue - text_offset_x_percent}%`;
                } else if (styles.left !== undefined && styles.left !== 'initial') {
                    const leftValue = parseFloat(String(styles.left));
                    styles.left = `${leftValue + text_offset_x_percent}%`;
                }
            }

            // Apply Y offset
            // Positive Y = move down, Negative Y = move up
            if (text_offset_y_percent !== 0) {
                if (styles.bottom !== undefined && styles.bottom !== 'initial') {
                    const bottomValue = parseFloat(String(styles.bottom));
                    styles.bottom = `${bottomValue + text_offset_y_percent}%`;
                } else if (styles.top !== undefined && styles.top !== 'initial') {
                    const topValue = parseFloat(String(styles.top));
                    styles.top = `${topValue - text_offset_y_percent}%`;
                }
            }

            return styles;
        }

        return { ...baseStyles, ...positionStyle };
    };

    // Get text alignment based on text_position
    const getTextAlignment = () => {
        if (!text_position) return 'center';

        if (text_position.includes('left')) return 'left';
        if (text_position.includes('right')) return 'right';
        return 'center';
    };

    // Get flex alignment based on text_position
    const getFlexAlignment = () => {
        // Vertical alignment
        let alignItems: 'flex-start' | 'center' | 'flex-end' = 'center';
        if (text_position.includes('top')) {
            alignItems = 'flex-start';
        } else if (text_position.includes('bottom')) {
            alignItems = 'flex-end';
        }

        // Horizontal alignment
        let justifyContent: 'flex-start' | 'center' | 'flex-end' = 'center';
        if (text_position.includes('left')) {
            justifyContent = 'flex-start';
        } else if (text_position.includes('right')) {
            justifyContent = 'flex-end';
        }

        return { alignItems, justifyContent };
    };

    // Calculate responsive font size
    const getResponsiveFontSize = () => {
        // Base calculation from text_width_percent (desktop size from Strapi)
        const desktopFontSize = text_width_percent / 100 * 5; // This is the exact Strapi value

        // Scale down for mobile devices
        // Desktop (1920px): 100% of base
        // Tablet (768px): ~60% of base  
        // Mobile (600px): ~45% of base
        // Small mobile (320px): ~30% of base
        return {
            xs: `${desktopFontSize * 0.30}rem`, // 320px-600px: 30% of desktop size
            sm: `${desktopFontSize * 0.45}rem`, // 600px-900px: 45% of desktop size
            md: `${desktopFontSize * 0.60}rem`, // 900px-1200px: 60% of desktop size
            lg: `${desktopFontSize * 0.75}rem`, // 1200px-1536px: 75% of desktop size
            xl: `${desktopFontSize}rem`,         // 1536px+: 100% - exact Strapi value
        };
    };

    return (
        <Box
            className={className}
            sx={{
                ...getPositionStyles(),
                display: 'flex',
                ...getFlexAlignment(),
                backgroundColor: background_color,
                zIndex: 10,
                padding: { xs: '1rem', sm: '1.25rem', md: '1.5rem', lg: '2rem' }, // Responsive padding
                ...(text_position?.includes('bottom') ? {
                    paddingBottom: 'max(1rem, calc(1rem + env(safe-area-inset-bottom)))',
                } : {}),
                ...(text_position?.includes('top') ? {
                    paddingTop: 'max(1rem, calc(1rem + env(safe-area-inset-top)))',
                } : {}),
            }}
        >
            {/* Inner wrapper for animation - doesn't affect positioning */}
            <Box ref={innerRef}>
                <Typography
                    variant="h3"
                    component="div"
                    sx={{
                        fontWeight: 300,
                        fontSize: getResponsiveFontSize(),
                        lineHeight: line_height,
                        whiteSpace: 'pre-line',
                        wordWrap: 'break-word',
                        textAlign: getTextAlignment(),
                        '& *': {
                            margin: 0,
                            padding: 0,
                        },
                        // Style HTML elements inside the content - proportionally scaled
                        '& h1': {
                            fontSize: { xs: '1.05rem', sm: '1.575rem', md: '2.1rem', lg: '2.625rem', xl: '3.5rem' },
                            fontWeight: 700,
                            marginBottom: { xs: '0.3rem', md: '0.6rem', xl: '1rem' },
                            lineHeight: 1.2,
                        },
                        '& h2': {
                            fontSize: { xs: '0.9rem', sm: '1.35rem', md: '1.8rem', lg: '2.25rem', xl: '3rem' },
                            fontWeight: 600,
                            marginBottom: { xs: '0.25rem', md: '0.5rem', xl: '0.75rem' },
                            lineHeight: 1.2,
                        },
                        '& h3': {
                            fontSize: { xs: '0.75rem', sm: '1.125rem', md: '1.5rem', lg: '1.875rem', xl: '2.5rem' },
                            fontWeight: 500,
                            marginBottom: { xs: '0.2rem', md: '0.4rem', xl: '0.6rem' },
                            lineHeight: 1.3,
                        },
                        '& h4': {
                            fontSize: { xs: '0.6rem', sm: '0.9rem', md: '1.2rem', lg: '1.5rem', xl: '2rem' },
                            fontWeight: 500,
                            marginBottom: { xs: '0.15rem', md: '0.3rem', xl: '0.5rem' },
                            lineHeight: 1.3,
                        },
                        '& h5': {
                            fontSize: { xs: '0.525rem', sm: '0.7875rem', md: '1.05rem', lg: '1.3125rem', xl: '1.75rem' },
                            fontWeight: 500,
                            marginBottom: { xs: '0.15rem', md: '0.25rem', xl: '0.4rem' },
                            lineHeight: 1.3,
                        },
                        '& h6': {
                            fontSize: { xs: '0.45rem', sm: '0.675rem', md: '0.9rem', lg: '1.125rem', xl: '1.5rem' },
                            fontWeight: 500,
                            marginBottom: { xs: '0.1rem', md: '0.2rem', xl: '0.35rem' },
                            lineHeight: 1.3,
                        },
                        '& p': {
                            fontSize: { xs: '0.45rem', sm: '0.675rem', md: '0.9rem', lg: '1.125rem', xl: '1.5rem' },
                            fontWeight: 300,
                            marginBottom: { xs: '0.3rem', md: '0.5rem', xl: '0.75rem' },
                            lineHeight: { xs: 1.4, md: 1.6 },
                        },
                        '& span': {
                            fontSize: 'inherit',
                        },
                        '& strong, & b': {
                            fontWeight: 600,
                        },
                        '& em, & i': {
                            fontStyle: 'italic',
                        },
                        '& ul, & ol': {
                            fontSize: { xs: '0.45rem', sm: '0.675rem', md: '0.9rem', lg: '1.125rem', xl: '1.5rem' },
                            paddingLeft: { xs: '0.75rem', md: '1rem', xl: '1.5rem' },
                            marginBottom: { xs: '0.3rem', md: '0.5rem', xl: '0.75rem' },
                        },
                        '& li': {
                            marginBottom: { xs: '0.15rem', md: '0.25rem', xl: '0.35rem' },
                            lineHeight: { xs: 1.4, md: 1.6 },
                        }
                    }}
                    dangerouslySetInnerHTML={{ __html: htmlContent }}
                />
            </Box>
        </Box>
    );
};

export default TextOverlay;
